import { AgentAvatar } from "./AgentAvatar";
import type { ReactElement } from "react";

/**
 * Extracted from `CelebrationBanner` (plan 435 M2, mfh-hero-component) so
 * `HomeHero`'s avatar row can reuse the exact same idiom (overlapping
 * circles, 5-visible cap, "+N" overflow pill, index-based colour cycle)
 * instead of forking it. `CelebrationBanner` now composes this component —
 * same markup, same classes, same `data-testid`.
 */
export interface CompetitionAgent {
  id: string;
  name?: string;
  photoUrl?: string;
}

export function AvatarStack({
  agents,
  ariaLabel,
}: {
  agents: CompetitionAgent[];
  ariaLabel?: string;
}): ReactElement {
  const visibleAgents = agents.slice(0, 5);
  const remaining = agents.length - visibleAgents.length;

  return (
    <div
      aria-label={ariaLabel ?? `${agents.length} competing agents`}
      className="hidden shrink-0 -space-x-2 sm:flex"
      data-testid="avatar-stack"
    >
      {visibleAgents.map((agent, index) => (
        <span
          className={`flex size-10 items-center justify-center rounded-full border-2 border-panel-ink text-xs font-bold text-primary-foreground ${
            index % 3 === 1
              ? "bg-info"
              : index % 3 === 2
                ? "bg-warning"
                : "bg-primary"
          }`}
          key={agent.id}
          title={agent.name ?? "Marketplace agent"}
        >
          <AgentAvatar name={agent.name ?? "Marketplace agent"} photoUrl={agent.photoUrl} imageSize={80}
            className="size-full rounded-full" />
        </span>
      ))}
      {remaining > 0 && (
        <span className="flex size-10 items-center justify-center rounded-full border-2 border-panel-ink bg-muted text-xs font-bold text-foreground">
          +{remaining}
        </span>
      )}
    </div>
  );
}
